import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Search, Edit, Trash2, Heart, HeartOff, Table, Grid3X3, ChevronsRight, ChevronsLeft, TrendingUp, AlertTriangle, PieChart, Calendar, Euro, Percent, Building2 } from "lucide-react";
import { masterSupabase as supabase } from "../lib/supabase-master.js";
import FinancingModal from "../components/FinancingModal";

const FinancingPage = () => {
  const [financings, setFinancings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFinancing, setSelectedFinancing] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalErrors, setModalErrors] = useState({});

  // Filter and view state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterOwner, setFilterOwner] = useState("");
  const [filterProperty, setFilterProperty] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem("financing-favorites");
    if (savedFavorites) {
      setFavorites(new Set(JSON.parse(savedFavorites)));
    }
  }, []);

  // Save favorites to localStorage
  const saveFavorites = (newFavorites) => {
    localStorage.setItem("financing-favorites", JSON.stringify([...newFavorites]));
    setFavorites(newFavorites);
  };

  // Toggle favorite
  const toggleFavorite = (financingId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(financingId)) {
      newFavorites.delete(financingId);
    } else {
      newFavorites.add(financingId);
    }
    saveFavorites(newFavorites);
  };

  // Lock/unlock body scroll when modal opens/closes
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  // LOAD DATA - BASED ON SCHEMA WITH PROPERTY ALLOCATIONS
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log("Loading data for user...");
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error("No authenticated user");
        }

        console.log("Loading data for user:", user.id);
        setCurrentUser(user);

        // Load financings - SIMPLIFIED APPROACH
        console.log("Loading financings...");
        const { data: financingsData, error: financingsError } = await supabase
          .from("financings")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (financingsError) {
          console.error("Error loading financings:", financingsError);
          throw financingsError;
        }
        console.log("Loaded financings:", financingsData?.length || 0);

        // Load properties
        console.log("Loading properties...");
        const { data: propertiesData, error: propertiesError } = await supabase
          .from("properties")
          .select("id, property_name, street, city, owner_id")
          .eq("created_by", user.id)
          .order("property_name");

        if (propertiesError) {
          console.error("Error loading properties:", propertiesError);
          throw propertiesError;
        }
        console.log("Loaded properties:", propertiesData?.length || 0);

        // Load owners
        console.log("Loading owners...");
        const { data: ownersData, error: ownersError } = await supabase
          .from("owners")
          .select("id, name, email")
          .eq("created_by", user.id)
          .order("name");

        if (ownersError) {
          console.error("Error loading owners:", ownersError);
          throw ownersError;
        }
        console.log("Loaded owners:", ownersData?.length || 0);

        // Load financing_properties separately
        console.log("Loading financing properties...");
        const { data: financingPropertiesData, error: financingPropertiesError } = await supabase
          .from("financing_properties")
          .select("*");
        
        if (financingPropertiesError) {
          console.error("Error loading financing properties:", financingPropertiesError);
          throw financingPropertiesError;
        }
        console.log("Loaded financing properties:", financingPropertiesData?.length || 0);

        // Combine data in frontend
        const enrichedFinancings = financingsData.map(financing => {
          const relatedProperties = financingPropertiesData
            .filter(fp => fp.financing_id === financing.id)
            .map(fp => {
              const property = propertiesData.find(p => p.id === fp.property_id);
              return {
                ...fp,
                properties: property
              };
            });
          
          return {
            ...financing,
            financing_properties: relatedProperties
          };
        });

        setFinancings(enrichedFinancings || []);
        setProperties(propertiesData || []);
        setOwners(ownersData || []);
        setError(null);
        console.log("Data loading completed successfully");
      } catch (error) {
        console.error("Error loading data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter financings - FIXED OWNER FILTER
  const filteredFinancings = useMemo(() => {
    let filtered = financings.filter(financing => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          financing.lender_name?.toLowerCase().includes(searchLower) ||
          financing.notes?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Type filter
      if (filterType && financing.repayment_type !== filterType) {
        return false;
      }

      // Owner filter - CORRECTED LOGIC
      if (filterOwner) {
        console.log("Filtering by owner:", filterOwner);
        if (financing.financing_properties && financing.financing_properties.length > 0) {
          const hasMatchingOwner = financing.financing_properties.some(fp => {
            const property = fp.properties;
            const matches = property && property.owner_id === filterOwner;
            console.log("Property owner check:", property?.property_name, property?.owner_id, "matches:", matches);
            return matches;
          });
          if (!hasMatchingOwner) {
            console.log("No matching owner for financing:", financing.lender_name);
            return false;
          }
        } else {
          console.log("No property allocations for financing:", financing.lender_name);
          return false;
        }
      }

      // Property filter - CORRECTED LOGIC
      if (filterProperty) {
        if (financing.financing_properties && financing.financing_properties.length > 0) {
          const hasMatchingProperty = financing.financing_properties.some(fp => 
            fp.property_id === filterProperty
          );
          if (!hasMatchingProperty) return false;
        } else {
          return false;
        }
      // Favorites filter
      if (showFavoritesOnly && !favorites.has(financing.id)) {
        return false;
      }
      // Expiring soon filter
      if (showExpiringOnly) {
        console.log("[DEBUG] Checking expiring filter for:", financing.lender_name, "End date:", financing.fixed_rate_end_date);
        const now = new Date();
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(now.getFullYear() + 1);
        if (!financing.fixed_rate_end_date) return false;
        console.log("[DEBUG] No end date, filtering out:", financing.lender_name);
        const endDate = new Date(financing.fixed_rate_end_date);
        console.log("[DEBUG] Date check:", financing.lender_name, "endDate:", endDate, "now:", now, "oneYear:", oneYearFromNow, "passes:", (endDate <= oneYearFromNow && endDate >= now));
        if (!(endDate <= oneYearFromNow && endDate >= now)) return false;
      }
      }

      return true;
    });

    console.log("Filtered financings:", filtered.length, "from", financings.length);
    return filtered;
  }, [financings, searchTerm, filterType, filterOwner, filterProperty, showFavoritesOnly, favorites, showExpiringOnly]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalAmount = filteredFinancings.reduce((sum, f) => sum + (parseFloat(f.loan_amount) || 0), 0);
    const count = filteredFinancings.length;
    const avgRate = count > 0 
      ? filteredFinancings.reduce((sum, f) => sum + (parseFloat(f.interest_rate_apy) || 0), 0) / count 
      : 0;

    // Count financings expiring soon (within 12 months)
    const now = new Date();
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(now.getFullYear() + 1);
    
    const expiringSoon = filteredFinancings.filter(f => {
      if (!f.fixed_rate_end_date) return false;
      const endDate = new Date(f.fixed_rate_end_date);
      return endDate <= oneYearFromNow && endDate >= now;
    }).length;

    return {
      totalAmount,
      count,
      avgRate,
      expiringSoon
    };
  }, [filteredFinancings]);

  // Handle save financing
  const handleSaveFinancing = async (formData) => {
    try {
      setModalLoading(true);
      setModalErrors({});

      if (!currentUser) {
        throw new Error("Nicht angemeldet");
      }

      // Prepare financing data
      const financingData = {
        lender_name: formData.lender_name,
        loan_amount: parseFloat(formData.loan_amount),
        interest_rate_apy: parseFloat(formData.interest_rate_apy),
        amortization_rate_apy: parseFloat(formData.amortization_rate_apy) || null,
        repayment_type: formData.repayment_type,
        start_date: formData.start_date || null,
        maturity_date: formData.maturity_date || null,
        fixed_rate_end_date: formData.fixed_rate_end_date || null,
        grace_months: parseInt(formData.grace_months) || 0,
        balloon_amount: parseFloat(formData.balloon_amount) || null,
        origination_fee: parseFloat(formData.origination_fee) || null,
        payment_frequency: formData.payment_frequency,
        index_ref: formData.index_ref || null,
        spread_bps: parseInt(formData.spread_bps) || null,
        notes: formData.notes || null,
        user_id: currentUser.id
      };

      let savedFinancing;

      if (selectedFinancing) {
        // Update existing financing
        const { data, error } = await supabase
          .from("financings")
          .update(financingData)
          .eq("id", selectedFinancing.id)
          .eq("user_id", currentUser.id)
          .select()
          .single();

        if (error) throw error;
        savedFinancing = data;

      // Handle property allocations
      if (formData.property_allocations && formData.property_allocations.length > 0) {
        // Delete existing allocations for this financing
        await supabase
          .from("financing_properties")
          .delete()
          .eq("financing_id", savedFinancing.id);

        // Insert new allocations
        const allocations = formData.property_allocations.map(allocation => ({
          financing_id: savedFinancing.id,
          property_id: allocation.property_id,
          allocation_ratio: allocation.allocation_ratio / 100
        }));

        const { error: allocError } = await supabase
          .from("financing_properties")
          .insert(allocations);

        if (allocError) {
          console.error("Error saving property allocations:", allocError);
        }
      }
      } else {
        // Create new financing
        const { data, error } = await supabase
          .from("financings")
          .insert([financingData])
          .select()
          .single();

        if (error) throw error;
        savedFinancing = data;

      // Handle property allocations
      if (formData.property_allocations && formData.property_allocations.length > 0) {
        // Delete existing allocations for this financing
        await supabase
          .from("financing_properties")
          .delete()
          .eq("financing_id", savedFinancing.id);

        // Insert new allocations
        const allocations = formData.property_allocations.map(allocation => ({
          financing_id: savedFinancing.id,
          property_id: allocation.property_id,
          allocation_ratio: allocation.allocation_ratio / 100
        }));

        const { error: allocError } = await supabase
          .from("financing_properties")
          .insert(allocations);

        if (allocError) {
          console.error("Error saving property allocations:", allocError);
        }
      }
      }

      // Reload data and close modal
      window.location.reload(); // Simple reload to ensure fresh data
      setIsModalOpen(false);
      setSelectedFinancing(null);

    } catch (error) {
      console.error("Error saving financing:", error);
      setModalErrors({ submit: error.message });
    } finally {
      setModalLoading(false);
    }
  };

  // Handle delete financing
  const handleDeleteFinancing = async (financing) => {
    if (!window.confirm(`Möchten Sie die Finanzierung "${financing.lender_name}" wirklich löschen?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("financings")
        .delete()
        .eq("id", financing.id)
        .eq("user_id", currentUser.id);

      if (error) throw error;

      window.location.reload(); // Simple reload to ensure fresh data
    } catch (error) {
      console.error("Error deleting financing:", error);
      alert("Fehler beim Löschen der Finanzierung: " + error.message);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format percentage
  const formatPercentage = (rate) => {
    return `${parseFloat(rate || 0).toFixed(2)}%`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("de-DE");
  };

  // Get property display name - CORRECTED
  const getPropertyDisplayName = (financing) => {
    if (!financing.financing_properties || financing.financing_properties.length === 0) {
      return "Keine Zuordnung";
    }
    
    const propertyNames = financing.financing_properties.map(fp => {
      const property = fp.properties;
      return property ? `${property.property_name} - ${property.city}` : "Unbekannt";
    });
    
    return propertyNames.join(", ");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Finanzierungen</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Verwalten Sie Ihre Immobilienfinanzierungen mit intelligenten Insights
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedFinancing(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Neue Finanzierung
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Gesamtvolumen</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(statistics.totalAmount)}
              </p>
            </div>
            <Euro className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Anzahl Finanzierungen</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.count}</p>
            </div>
            <Building2 className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ø Zinssatz</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatPercentage(statistics.avgRate)}
              </p>
            </div>
            <Percent className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <button onClick={() => setShowExpiringOnly(!showExpiringOnly)} className={`w-full text-left bg-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 ${showExpiringOnly ? "ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-900/20" : ""}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Läuft bald aus</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.expiringSoon}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Bank oder Objekt suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
          >
            <option value="">Typ</option>
            <option value="annuity">Annuität</option>
            <option value="interest_only">Endfällig</option>
            <option value="linear">Linear</option>
            <option value="balloon">Balloon</option>
          </select>

          <select
            value={filterOwner}
            onChange={(e) => setFilterOwner(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
          >
            <option value="">Eigentümer</option>
            {owners.map(owner => (
              <option key={owner.id} value={owner.id}>{owner.name}</option>
            ))}
          </select>

          <select
            value={filterProperty}
            onChange={(e) => setFilterProperty(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white"
          >
            <option value="">Alle Objekte ({properties.length})</option>
            {properties.map(property => (
              <option key={property.id} value={property.id}>
                {property.property_name} - {property.city}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                showFavoritesOnly
                  ? "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                  : "bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              <Heart className={`w-4 h-4 ${showFavoritesOnly ? "fill-current" : ""}`} />
              Favoriten
            </button>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "table"
                  ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <Table className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "cards"
                  ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <Grid3X3 className="w-5 h-5" />
            </button>
          </div>

          {viewMode === "table" && (
            <button
              onClick={() => setIsTableExpanded(!isTableExpanded)}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              {isTableExpanded ? (
                <>
                  <ChevronsLeft className="w-4 h-4" />
                  Reduzieren
                </>
              ) : (
                <>
                  <ChevronsRight className="w-4 h-4" />
                  Erweitern
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {viewMode === "table" ? (
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className={`overflow-x-auto ${isTableExpanded ? "" : "max-w-full"}`}>
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Bank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Darlehenssumme
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Zinssatz
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Typ
                  </th>
                  {isTableExpanded && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tilgungssatz
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Laufzeit
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Objekte
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Notizen
                      </th>
                    </>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredFinancings.map((financing) => (
                  <tr key={financing.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleFavorite(financing.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          {favorites.has(financing.id) ? (
                            <Heart className="w-4 h-4 fill-current text-red-500" />
                          ) : (
                            <HeartOff className="w-4 h-4" />
                          )}
                        </button>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {financing.lender_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-900 dark:text-white font-medium">
                      {formatCurrency(financing.loan_amount)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-900 dark:text-white">
                          {formatPercentage(financing.interest_rate_apy)}
                        </span>
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                        {financing.repayment_type === "annuity" ? "Annuität" :
                         financing.repayment_type === "interest_only" ? "Endfällig" :
                         financing.repayment_type === "linear" ? "Linear" : "Balloon"}
                      </span>
                    </td>
                    {isTableExpanded && (
                      <>
                        <td className="px-4 py-4 text-gray-900 dark:text-white">
                          {formatPercentage(financing.amortization_rate_apy)}
                        </td>
                        <td className="px-4 py-4 text-gray-900 dark:text-white">
                          <div className="text-sm">
                            <div>Start: {formatDate(financing.start_date)}</div>
                            <div>Ende: {formatDate(financing.maturity_date)}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-gray-900 dark:text-white text-sm">
                          {getPropertyDisplayName(financing)}
                        </td>
                        <td className="px-4 py-4 text-gray-900 dark:text-white text-sm">
                          {financing.notes ? (
                            <div className="max-w-xs truncate" title={financing.notes}>
                              {financing.notes}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedFinancing(financing);
                            setIsModalOpen(true);
                          }}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFinancing(financing)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Cards View
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFinancings.map((financing) => (
            <div key={financing.id} className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleFavorite(financing.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    {favorites.has(financing.id) ? (
                      <Heart className="w-4 h-4 fill-current text-red-500" />
                    ) : (
                      <HeartOff className="w-4 h-4" />
                    )}
                  </button>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {financing.lender_name}
                  </h3>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                  {financing.repayment_type === "annuity" ? "Annuität" :
                   financing.repayment_type === "interest_only" ? "Endfällig" :
                   financing.repayment_type === "linear" ? "Linear" : "Balloon"}
                </span>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Darlehenssumme:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(financing.loan_amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Zinssatz:</span>
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatPercentage(financing.interest_rate_apy)}
                    </span>
                    <TrendingUp className="w-3 h-3 text-green-500" />
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Objekte:</span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {getPropertyDisplayName(financing)}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setSelectedFinancing(financing);
                    setIsModalOpen(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Bearbeiten
                </button>
                <button
                  onClick={() => handleDeleteFinancing(financing)}
                  className="px-3 py-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredFinancings.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Keine Finanzierungen gefunden
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchTerm || filterType || filterOwner || filterProperty || showFavoritesOnly
              ? "Versuchen Sie, Ihre Filter zu ändern oder zu entfernen."
              : "Erstellen Sie Ihre erste Finanzierung, um zu beginnen."}
          </p>
          {!searchTerm && !filterType && !filterOwner && !filterProperty && !showFavoritesOnly && (
            <button
              onClick={() => {
                setSelectedFinancing(null);
                setIsModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Erste Finanzierung erstellen
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      <FinancingModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFinancing(null);
          setModalErrors({});
        }}
        financing={selectedFinancing}
        properties={properties}
        onSave={handleSaveFinancing}
        loading={modalLoading}
        errors={modalErrors}
        existingFinancings={financings}
      />
    </div>
  );
};

export default FinancingPage;
